import SchemaValidator from "util/schema-helpers/SchemaValidator";
import { ThunkDispatch, UnknownAction } from "@reduxjs/toolkit";
import actions from "rdx/actions";
import { Token } from "@stripe/stripe-js";
import { camelizeKeys, keysToSnakeCase } from "lib/helpers/convertObjKeys";
import SessionClient from "util/SessionClient";
import {
  GetEnrollmentResponse,
  UncompEnrollmentResponseBodyT,
  UncompEnrollmentResponseT,
  UpdateEnrollmentResponse,
  UpgradePreviewResponse,
} from "./types";
import messages from "./messages";
import {
  GetEnrollmentResponseBodySchema,
  ServiceSlugs,
  UncompEnrollmentResponseBodySchema,
  UpdateEnrollmentResponseBodySchema,
  UpgradePreviewResponseSchema,
  CompServiceSlugs,
  EntServiceSlugs,
  EntStaffSlugs,
} from "./schemas";
import { api } from "../../services/api";

export const ENROLLMENT_TAGS = {
  ENROLLMENTS: "Enrollments",
} as const;

const getEnrollmentResponse = (res: GetEnrollmentResponse) => {
  SchemaValidator.validate<typeof GetEnrollmentResponseBodySchema>({
    schema: GetEnrollmentResponseBodySchema,
    schemaName: "GetEnrollmentResponseBodySchema: getEnrollment",
    dto: camelizeKeys(res.body),
  });

  return camelizeKeys(res.body);
};

type AlertMessagesOptionsT = {
  preferServerMessage?: boolean;
};

const getServerErrorMessage = (error: unknown) =>
  (error as { error?: { data?: { error?: { message?: string } } } } | undefined)?.error?.data?.error?.message;

const alertMessages = async (
  error: unknown,
  dispatch: ThunkDispatch<unknown, unknown, UnknownAction>,
  msg?: string,
  options?: AlertMessagesOptionsT,
) => {
  const serverMessage = getServerErrorMessage(error);
  const [primary, secondary] = options?.preferServerMessage ? [serverMessage, msg] : [msg, serverMessage];

  dispatch(
    actions.setAlertMessageVisible({
      message: primary ?? secondary ?? messages.ENROLLMENT_ERROR,
      severity: "error",
    }),
  );
};

export const enrollmentApi = api
  .enhanceEndpoints({ addTagTypes: [...Object.values(ENROLLMENT_TAGS)] })
  .injectEndpoints({
    endpoints: (b) => ({
      compEnterpriseServices: b.mutation<
        { status: number },
        { enterpriseId: number; userId: number; services: CompServiceSlugs[] }
      >({
        query: ({ enterpriseId, userId, services }) => ({
          url: "/seats/comp",
          method: "POST",
          body: keysToSnakeCase({ enterprise_id: enterpriseId, user_id: userId, services }),
        }),
        onQueryStarted: async (_arg, { queryFulfilled, dispatch }) => {
          try {
            const { data } = await queryFulfilled;
            if (data.status === 200) {
              dispatch(
                actions.setAlertMessageVisible({
                  message: "Successfully comped the services",
                  severity: "success",
                }),
              );
            }
          } catch (error) {
            await alertMessages(error, dispatch);
          }
        },
      }),
      compService: b.mutation<{ status: number }, { userId: number; serviceSlug: CompServiceSlugs }>({
        query: ({ userId, serviceSlug }) => ({
          url: `/services/${serviceSlug}/enrollment/comp`,
          method: "POST",
          body: keysToSnakeCase({ user_id: userId }),
        }),
        onQueryStarted: async (_arg, { queryFulfilled, dispatch }) => {
          try {
            const { data } = await queryFulfilled;
            if (data.status === 200) {
              dispatch(
                actions.setAlertMessageVisible({
                  message: "Successfully comped the services",
                  severity: "success",
                }),
              );
            }
          } catch (error) {
            await alertMessages(error, dispatch);
          }
        },
      }),
      uncompEnrollment: b.mutation<UncompEnrollmentResponseBodyT, { userId: number; serviceSlug: string }>({
        query: ({ userId, serviceSlug }) => ({
          url: `/services/${serviceSlug}/enrollment/uncomp`,
          method: "POST",
          body: keysToSnakeCase({ user_id: userId }),
        }),
        transformResponse: (res: UncompEnrollmentResponseT) => {
          SchemaValidator.validate<typeof UncompEnrollmentResponseBodySchema>({
            schema: UncompEnrollmentResponseBodySchema,
            schemaName: "UncompEnrollmentResponseBodySchema: uncompEnrollment",
            dto: camelizeKeys(res.body),
          });

          return camelizeKeys(res.body);
        },
        onQueryStarted: async ({ userId }, { queryFulfilled, dispatch }) => {
          try {
            dispatch(
              actions.setAlertMessageVisible({
                message: "Submitting. . .",
                severity: "info",
                duration: 6000,
              }),
            );
            const { data } = await queryFulfilled;
            // Temporary bridge: keep the legacy admin user store in sync until the admin views consume RTK Query
            dispatch(actions.setDetailInUserDetails({ enrollment: data.enrollment }));
            dispatch(actions.setDetailInUsersList({ enrollment: data.enrollment, id: userId }));
            dispatch(
              actions.setAlertMessageVisible(
                data.enrollment.paymentRequired === true
                  ? { message: messages.UNCOMP_PAYMENT_REQUIRED, severity: "warning" }
                  : { message: messages.UNCOMP_SUCCESS, severity: "success" },
              ),
            );
          } catch (error) {
            await alertMessages(error, dispatch, messages.ERROR_UNCOMPING_ENROLLMENT, { preferServerMessage: true });
          }
        },
      }),
      getEnrollment: b.query<GetEnrollmentResponse["body"], void>({
        providesTags: [ENROLLMENT_TAGS.ENROLLMENTS],
        query: () => "/enrollment",
        transformResponse: getEnrollmentResponse,
        onQueryStarted: async (_arg, { queryFulfilled, dispatch }) => {
          try {
            await queryFulfilled;
          } catch (error) {
            await alertMessages(error, dispatch, "There was an error fetching enrollment details.");
          }
        },
      }),
      upgradePreview: b.mutation<
        UpgradePreviewResponse,
        { currentService: ServiceSlugs; toService: ServiceSlugs | EntServiceSlugs | EntStaffSlugs | CompServiceSlugs }
      >({
        query: ({ currentService, toService }) => ({
          url: `/services/${currentService}/enrollment/upgrade`,
          method: "POST",
          body: keysToSnakeCase({ preview: true, to_service: toService }),
        }),
        transformResponse: (res: UpgradePreviewResponse) => {
          SchemaValidator.validate<typeof UpgradePreviewResponseSchema>({
            schema: UpgradePreviewResponseSchema,
            schemaName: "UpgradePreviewResponseSchema",
            dto: camelizeKeys(res),
          });
          return camelizeKeys(res);
        },
        onQueryStarted: async (_arg, { queryFulfilled, dispatch }) => {
          try {
            await queryFulfilled;
          } catch (error) {
            await alertMessages(error, dispatch, "There was an error fetching upgrade details.");
          }
        },
      }),
      upgradeEnrollment: b.mutation<
        GetEnrollmentResponse["body"],
        { service: ServiceSlugs; params: { prorationKey?: string; toService: ServiceSlugs } }
      >({
        query: ({ service, params }) => ({
          url: `/services/${service}/enrollment/upgrade`,
          method: "POST",
          body: keysToSnakeCase(params),
        }),
        transformResponse: getEnrollmentResponse,
        onQueryStarted: async (_arg, { queryFulfilled, dispatch }) => {
          try {
            dispatch(
              actions.setAlertMessageVisible({
                message: "Submitting. . .",
                severity: "info",
                duration: 6000,
              }),
            );
            const { data } = await queryFulfilled;
            dispatch(
              enrollmentApi.util.updateQueryData("getEnrollment", undefined, (state) => {
                Object.assign(state, data);
              }),
            );
            dispatch(
              actions.setAlertMessageVisible({
                message: messages.ENROLLMENT_UPDATE_SUCCESS,
                severity: "success",
              }),
            );
          } catch (error: unknown) {
            await alertMessages(error, dispatch);
          }
        },
      }),
      downgradeEnrollment: b.mutation<
        GetEnrollmentResponse["body"],
        { service?: ServiceSlugs; params: { toService: ServiceSlugs } }
      >({
        query: ({ service, params }) => ({
          url: `/services/${service}/enrollment/downgrade`,
          method: "POST",
          body: keysToSnakeCase(params),
        }),
        transformResponse: getEnrollmentResponse,
        onQueryStarted: async (_arg, { queryFulfilled, dispatch }) => {
          try {
            dispatch(
              actions.setAlertMessageVisible({
                message: "Submitting. . .",
                severity: "info",
                duration: 6000,
              }),
            );
            const { data } = await queryFulfilled;
            dispatch(
              enrollmentApi.util.updateQueryData("getEnrollment", undefined, (state) => {
                Object.assign(state, data);
              }),
            );
            dispatch(actions.setEnterpriseOrgSeats({ seats: [] }));
            dispatch(actions.setEnterpriseOrgSeat({ settings: {} }));
            dispatch(actions.setEnterpriseOrg({ settings: {} }));
            dispatch(
              actions.setAlertMessageVisible({
                message: messages.ENROLLMENT_UPDATE_SUCCESS,
                severity: "success",
              }),
            );
          } catch (error: unknown) {
            await alertMessages(error, dispatch);
          }
        },
      }),
      updateEnrollment: b.mutation<UpdateEnrollmentResponse["body"], { tokenId: Token["id"]; tos: boolean }>({
        query: ({ tokenId, tos }) => ({
          url: "/enrollment",
          method: "PATCH",
          body: keysToSnakeCase({ token: tokenId, tos, sourceType: "credit_card" }),
        }),
        transformResponse: (res: UpdateEnrollmentResponse) => {
          const result = SchemaValidator.validate<typeof UpdateEnrollmentResponseBodySchema>({
            schema: UpdateEnrollmentResponseBodySchema,
            schemaName: "UpdateEnrollmentResponseBodySchema: updateEnrollment",
            dto: camelizeKeys(res.body),
          });

          if (!result.success) {
            throw new Error("Schema validation failed for response data: UpdateEnrollmentResponseBodySchema");
          }

          return camelizeKeys(result.data);
        },
        invalidatesTags: [ENROLLMENT_TAGS.ENROLLMENTS],
        onQueryStarted: async (_arg, { queryFulfilled, dispatch }) => {
          try {
            dispatch(
              actions.setAlertMessageVisible({
                message: "Submitting. . .",
                severity: "info",
                duration: 6000,
              }),
            );
            const { data } = await queryFulfilled;
            dispatch(
              enrollmentApi.util.updateQueryData("getEnrollment", undefined, (state) => {
                if (data.enrollment) {
                  Object.assign(state.enrollment || {}, data.enrollment);
                }
                if (data.cardInfo) {
                  Object.assign(state.cardInfo || {}, data.cardInfo);
                }
              }),
            );
            if (data.banners) {
              const session = new SessionClient();
              session.banners = data.banners;
              dispatch(actions.setBanners(data.banners));
            }
            dispatch(
              actions.setAlertMessageVisible({
                message: messages.ENROLLMENT_UPDATE_SUCCESS,
                severity: "success",
              }),
            );
          } catch (error) {
            await alertMessages(error, dispatch);
          }
        },
      }),
      upgradeFromEntApplication: b.mutation<GetEnrollmentResponse["body"], { tokenId: Token["id"]; tos: boolean }>({
        query: ({ tokenId, tos }) => ({
          url: "services/powur_enterprise_pro/enrollment/upgrade_from_enterprise_application",
          method: "POST",
          body: keysToSnakeCase({ token: tokenId, tos, sourceType: "credit_card" }),
        }),
        transformResponse: getEnrollmentResponse,
        invalidatesTags: [ENROLLMENT_TAGS.ENROLLMENTS],
        onQueryStarted: async (_arg, { queryFulfilled, dispatch }) => {
          try {
            dispatch(
              actions.setAlertMessageVisible({
                message: "Submitting. . .",
                severity: "info",
                duration: 6000,
              }),
            );
            const { data } = await queryFulfilled;
            dispatch(
              enrollmentApi.util.updateQueryData("getEnrollment", undefined, (state) => {
                if (data.enrollment) {
                  Object.assign(state.enrollment || {}, data.enrollment);
                }
                if (data.cardInfo) {
                  Object.assign(state.cardInfo || {}, data.cardInfo);
                }
              }),
            );
            dispatch(
              actions.setAlertMessageVisible({
                message: "Successfully activated your Enterprise Subscription",
                severity: "success",
              }),
            );
            dispatch(actions.setUserRequirePaymentData(false));
          } catch (error) {
            await alertMessages(error, dispatch);
          }
        },
      }),
      unseatFromEnterprise: b.mutation<{ status: number }, { seatId: number }>({
        query: ({ seatId }) => ({
          url: `/seats/${seatId}`,
          method: "PATCH",
          body: keysToSnakeCase({ services: [] }),
        }),
        onQueryStarted: (_queryParams, { dispatch, queryFulfilled }) => {
          dispatch(
            actions.setAlertMessageVisible({
              message: "Updating. . .",
              severity: "info",
              duration: 6000,
            }),
          );
          queryFulfilled
            .then(() => {
              const session = new SessionClient();
              session.user?.reload("unseat-enterprise");
              dispatch(
                actions.setAlertMessageVisible({
                  message: "User successfully unseated from Enterprise",
                  severity: "success",
                }),
              );
            })
            .catch(() => {
              dispatch(
                actions.setAlertMessageVisible({
                  message: "Failed to unseat user from Enterprise",
                  severity: "error",
                }),
              );
            });
        },
        invalidatesTags: [ENROLLMENT_TAGS.ENROLLMENTS],
      }),
    }),
  });

export const {
  useGetEnrollmentQuery,
  useUpgradeEnrollmentMutation,
  useUpdateEnrollmentMutation,
  useDowngradeEnrollmentMutation,
  useUpgradePreviewMutation,
  useUpgradeFromEntApplicationMutation,
  useCompEnterpriseServicesMutation,
  useCompServiceMutation,
  useUncompEnrollmentMutation,
  useUnseatFromEnterpriseMutation,
} = enrollmentApi;
